<template>
  <div
    class="sv-hc"
    :class="[`sv-hc--${variant}`, { 'sv-hc--touch': touch }]"
    role="dialog"
    :aria-label="`${name} — visit checklist`"
    data-test="sv-checklist-card"
  >
    <!-- The operatory's colour, so the card reads as belonging to the chair it came from. -->
    <span v-if="variant !== 'sheet'" class="sv-hc__top" :style="{ background: accent }" aria-hidden="true"></span>
    <div v-if="variant === 'sheet'" class="sv-hc__grab" aria-hidden="true"></div>

    <div class="sv-hc__head">
      <div class="sv-hc__who">
        <span class="sv-hc__name" data-test="sv-checklist-name">{{ name }}</span>
        <span v-if="ageText" class="sv-hc__age" data-test="sv-checklist-age">{{ ageText }}</span>
        <span
          v-if="pillText"
          class="sv-hc__status"
          :class="`sv-hc__status--${pillKey}`"
          data-test="sv-checklist-status"
        >{{ pillText }}</span>
        <button
          v-if="closable"
          type="button"
          class="sv-hc__x"
          aria-label="Close checklist"
          data-test="sv-checklist-close"
          @click="$emit('close')"
        ><i class="fa fa-times" aria-hidden="true"></i></button>
      </div>

      <div class="sv-hc__when" data-test="sv-checklist-when">
        <span v-if="timeRange" class="sv-hc__tm">{{ timeRange }}</span>
        <template v-if="timeRange">
          <span class="sv-hc__sep" aria-hidden="true"></span><span>{{ minutes }} min</span>
        </template>
        <template v-if="opName">
          <span v-if="timeRange" class="sv-hc__sep" aria-hidden="true"></span><span>{{ opName }}</span>
        </template>
        <template v-if="providerName">
          <span v-if="timeRange || opName" class="sv-hc__sep" aria-hidden="true"></span><span>{{ providerName }}</span>
        </template>
      </div>

      <div v-if="procedures.length" class="sv-hc__procs" data-test="sv-checklist-procs">
        <span v-for="(p, i) in procedures" :key="i" class="sv-hc__proc">{{ p }}</span>
      </div>
    </div>

    <template v-if="categories.length">
      <div
        class="sv-hc__prog"
        :class="{ 'sv-hc__prog--done': progress.kind === 'all' }"
        data-test="sv-checklist-progress"
      >
        <span class="sv-hc__prog-t">
          <span v-if="progress.kind === 'none'" class="sv-hc__mut">No to-dos</span>
          <span v-else-if="progress.kind === 'all'" class="sv-hc__ok">
            <i class="fa fa-check" aria-hidden="true"></i>All {{ progress.todo }} {{ noun }} done
          </span>
          <span v-else><b>{{ progress.left }}</b> to do<span v-if="progress.done > 0" class="sv-hc__mut"> · {{ progress.done }} done</span></span>
          <span v-if="progress.fyi > 0" class="sv-hc__mut">· {{ progress.fyi }} FYI</span>
        </span>
        <span v-if="segments.length" class="sv-hc__bar" aria-hidden="true">
          <i v-for="seg in segments" :key="seg.id" :class="{ on: seg.on }"></i>
        </span>
      </div>

      <div
        ref="body"
        class="sv-hc__body"
        :class="{ 'sv-hc__body--more': more }"
        data-test="sv-checklist-body"
        @scroll="updateMore"
      >
        <section
          v-for="c in categories"
          :key="c.key"
          class="sv-hc-cat"
          :class="[`sv-hc-cat--${c.key}`, { 'sv-hc-cat--focus': focusCategory === c.key }]"
          :data-cat="c.key"
          :data-test="`sv-checklist-cat-${c.key}`"
        >
          <div class="sv-hc-cat__h">
            <span class="sv-hc-cat__tag"><i class="fa" :class="c.icon" aria-hidden="true"></i>{{ c.label }}</span>
            <span class="sv-hc-cat__sp"></span>
            <span v-if="isComplete(c)" class="sv-hc-cat__done" :data-test="`sv-checklist-done-${c.key}`">
              <i class="fa fa-check" aria-hidden="true"></i>Done
            </span>
            <button
              v-if="c.tab"
              type="button"
              class="sv-hc-cat__go"
              :title="`Open ${c.label.toLowerCase()} in patient profile`"
              :aria-label="`Open ${c.label.toLowerCase()} in patient profile`"
              :data-test="`sv-checklist-go-${c.key}`"
              @click="$emit('open-tab', c.tab)"
            ><i class="fa fa-arrow-right" aria-hidden="true"></i></button>
          </div>

          <div class="sv-hc-cat__items">
            <template v-for="it in c.items">
              <!-- A to-do: a real checkbox. -->
              <button
                v-if="it.kind === 'action'"
                :key="it.id"
                type="button"
                role="checkbox"
                :aria-checked="isChecked(it) ? 'true' : 'false'"
                class="sv-hc-item sv-hc-item--act"
                :class="{ 'sv-hc-item--done': isChecked(it) }"
                :data-test="`sv-checklist-item-${it.id}`"
                @click="$emit('toggle', { itemKey: it.id, checked: !isChecked(it) })"
              >
                <span class="sv-hc-cb"><i v-if="isChecked(it)" class="fa fa-check" aria-hidden="true"></i></span>
                <span class="sv-hc-txt">
                  <span class="t">{{ it.title }}</span>
                  <span v-if="it.sub" class="s">{{ it.sub }}</span>
                </span>
                <span v-if="it.meta" class="sv-hc-meta" :class="it.meta.tone ? `sv-hc-meta--${it.meta.tone}` : ''">{{ it.meta.text }}</span>
                <span v-else></span>
              </button>

              <!--
                An FYI: NOT a control. Premedication lives here — OD records that it is REQUIRED and
                nothing records that it was taken, so a checkbox would read as "taken".
              -->
              <div
                v-else
                :key="it.id"
                class="sv-hc-item sv-hc-item--info"
                :class="it.tone ? `sv-hc-item--${it.tone}` : ''"
                :data-test="`sv-checklist-info-${it.id}`"
              >
                <span class="sv-hc-fyi" :class="it.tone ? `sv-hc-fyi--${it.tone}` : ''" aria-hidden="true">!</span>
                <span class="sv-hc-txt">
                  <span class="t">{{ it.title }}</span>
                  <span v-if="it.sub" class="s">{{ it.sub }}</span>
                </span>
                <span v-if="it.meta" class="sv-hc-meta" :class="it.meta.tone ? `sv-hc-meta--${it.meta.tone}` : ''">{{ it.meta.text }}</span>
                <span v-else></span>
              </div>
            </template>
          </div>
        </section>
      </div>
    </template>

    <!--
      Nothing in the checklist. Said only when that is TRUE: before the alerts arrive, or when a
      rule failed to run, "nothing open" would be a confident claim made from missing data.
    -->
    <div v-else class="sv-hc__clear" :class="{ 'sv-hc__clear--unsure': pending || incomplete }" data-test="sv-checklist-empty">
      <span class="ic"><i class="fa" :class="pending || incomplete ? 'fa-question' : 'fa-check'" aria-hidden="true"></i></span>
      <div>
        <div class="t">{{ emptyTitle }}</div>
        <div class="s">{{ emptySub }}</div>
      </div>
    </div>

    <div class="sv-hc__foot">
      <button
        type="button"
        class="sv-hc__open"
        data-test="sv-checklist-open-profile"
        @click="$emit('open-tab', 'overview')"
      >Open patient profile<i class="fa fa-arrow-right" aria-hidden="true"></i></button>
    </div>
  </div>
</template>

<script>
/**
 * The visit checklist card (Phase 10e) — the appointment's header, then its alerts regrouped by what
 * the front desk does about them, with to-dos you can tick.
 *
 * PRESENTATIONAL. Props in, events out, no store: ChecklistLayer owns where the card is, whether it
 * is open, and what a tick does. That is what lets the same card be a hover tooltip on a desktop, an
 * anchored popover on an iPad and a bottom sheet on a phone (`variant`).
 *
 * ⛔ `kind: 'info'` lines are never controls. Premedication is one: it is an FYI because nothing in
 * Open Dental records that it was taken. checklist.js decides the kind; this file only honours it.
 *
 * ⛔ Totals come from checklistTotals() — the same function the chip and (Phase 11) the table ring
 * use — fed the ticks for THIS card's items only. A tick left behind by an item that has since
 * cleared is not in `categories`, so it cannot count, and the bar cannot read past 100%.
 *
 * ⛔ No prop may be named `patNum`, `siteID`, `loading` or `allSettings`: mixin.js defines computeds
 * with those names and a computed SILENTLY REPLACES a same-named prop.
 */
import { checklistTotals, checkKey } from '@/utils/scheduleView/checklist';
import { progressParts, todoNoun } from '@/utils/scheduleView/checklistLayer';
import {
  patientDisplayName, appointmentSpan, appointmentLength, formatTimeRange,
} from '@/utils/scheduleView/layout';

export default {
  name: 'VisitChecklistCard',

  props: {
    /** One appointment from /schedule-view/day. */
    appt: { type: Object, required: true },
    /** buildChecklist() output: [{ key, label, icon, tab, items }]. */
    categories: { type: Array, default: () => [] },
    /** itemId -> truthy for the items ticked on THIS appointment. */
    checked: { type: Object, default: () => ({}) },
    /** 'hover' (a desktop tooltip) | 'pop' (touch popover) | 'sheet' (phone bottom sheet). */
    variant: { type: String, default: 'hover' },
    /** The category key to highlight and scroll to (an alert icon was hovered). */
    focusCategory: { type: String, default: null },
    /** The operatory's accent colour, hex. */
    accent: { type: String, default: '#9ca4a2' },
    opName: { type: String, default: '' },
    providerName: { type: String, default: '' },
    /** The status wording and bucket the block shows. */
    statusText: { type: String, default: '' },
    bucket: { type: String, default: 'unconfirmed' },
    broken: { type: Boolean, default: false },
    /** The alerts have not arrived yet: an empty checklist must not claim the visit is clean. */
    pending: { type: Boolean, default: false },
    /** Some alert rules failed to run: an empty checklist must not claim the visit is clean. */
    incomplete: { type: Boolean, default: false },
  },

  data() {
    return { more: false };
  },

  computed: {
    touch() {
      return this.variant !== 'hover';
    },

    closable() {
      return this.variant !== 'hover';
    },

    name() {
      return patientDisplayName(this.appt);
    },

    /** Only a real number: a missing age prints nothing, never "null yrs". */
    ageText() {
      const age = this.appt && this.appt.patient ? this.appt.patient.Age : null;
      if (typeof age !== 'number' || !Number.isFinite(age)) return '';
      return age === 1 ? '1 yr' : `${age} yrs`;
    },

    pillText() {
      return this.broken ? 'Broken' : this.statusText;
    },

    pillKey() {
      if (this.broken) return 'broken';
      return ['confirmed', 'arrived', 'completed', 'unconfirmed'].indexOf(this.bucket) !== -1
        ? this.bucket
        : 'unconfirmed';
    },

    timeRange() {
      const span = appointmentSpan(this.appt);
      return span ? formatTimeRange(span.start, span.end) : '';
    },

    minutes() {
      return appointmentLength(this.appt);
    },

    /** ProcDescript is one comma-separated string — the payload has no procedure list or codes. */
    procedures() {
      return String((this.appt && this.appt.ProcDescript) || '')
        .split(',')
        .map(p => p.trim())
        .filter(Boolean);
    },

    totals() {
      const marks = {};
      Object.keys(this.checked || {}).forEach((id) => {
        if (this.checked[id]) marks[checkKey(0, id)] = true;
      });
      return checklistTotals(this.categories, marks, 0);
    },

    progress() {
      return progressParts(this.totals);
    },

    noun() {
      return todoNoun(this.progress.todo);
    },

    /** One segment per to-do, in checklist order, filled when ticked. */
    segments() {
      const out = [];
      this.categories.forEach((c) => {
        c.items.forEach((it) => {
          if (it.kind === 'action') out.push({ id: it.id, on: !!this.checked[it.id] });
        });
      });
      return out;
    },

    emptyTitle() {
      if (this.pending) return 'Still checking this visit';
      if (this.incomplete) return 'Nothing found for this visit';
      return 'Nothing open for this visit';
    },

    emptySub() {
      if (this.pending) return 'Alerts have not loaded yet';
      if (this.incomplete) return 'Some checks could not run — open the profile to be sure';
      return 'No alerts, forms, tasks or balances';
    },
  },

  watch: {
    focusCategory() {
      this.$nextTick(this.scrollToFocus);
    },
  },

  mounted() {
    this.updateMore();
    this.scrollToFocus();
  },

  updated() {
    this.updateMore();
  },

  methods: {
    isChecked(item) {
      return !!(this.checked && this.checked[item.id]);
    },

    /** "Done" appears only for a category that HAS to-dos, all of them ticked. */
    isComplete(category) {
      const actions = category.items.filter(it => it.kind === 'action');
      return actions.length > 0 && actions.every(it => this.isChecked(it));
    },

    /** Shows the fade at the bottom while there is more to scroll to. */
    updateMore() {
      const b = this.$refs.body;
      this.more = !!b && b.scrollHeight - b.scrollTop - b.clientHeight > 4;
    },

    /** Scrolls the highlighted category into view if it is not already (an icon was hovered). */
    scrollToFocus() {
      const b = this.$refs.body;
      if (!b || !this.focusCategory) return;
      const sec = b.querySelector(`[data-cat="${this.focusCategory}"]`);
      if (!sec) return;
      const top = sec.offsetTop - 6;
      const bottom = sec.offsetTop + sec.offsetHeight + 6;
      if (top >= b.scrollTop && bottom <= b.scrollTop + b.clientHeight) return;
      const target = Math.max(0, top);
      // Element.scrollTo is missing on older iPads' Safari (and in jsdom): assign scrollTop instead.
      if (typeof b.scrollTo === 'function') b.scrollTo({ top: target, behavior: 'smooth' });
      else b.scrollTop = target;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-hc {
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  background: $sv-surface;
  border: 1px solid $sv-line;
  border-radius: 14px;
  box-shadow: 0 24px 56px -22px rgba(21, 32, 26, 0.34), 0 4px 12px -6px rgba(21, 32, 26, 0.14);
  overflow: hidden;
  color: $sv-ink;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  animation: sv-hc-in 0.16s cubic-bezier(0.2, 0.7, 0.3, 1);
}

@keyframes sv-hc-in {
  from { opacity: 0; transform: translateY(4px) scale(0.985); }
  to { opacity: 1; transform: none; }
}

@keyframes sv-hc-up {
  from { opacity: 0.3; transform: translateY(48px); }
  to { opacity: 1; transform: none; }
}

@keyframes sv-hc-tick {
  from { opacity: 0; transform: scale(0.3); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .sv-hc,
  .sv-hc-item--done .sv-hc-cb .fa { animation: none; }
}

.sv-hc__top {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
}

.sv-hc--sheet {
  border: 0;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -12px 40px -16px rgba(21, 32, 26, 0.4);
  animation: sv-hc-up 0.26s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.sv-hc__grab {
  flex: 0 0 auto;
  width: 38px;
  height: 5px;
  margin: 8px auto 0;
  border-radius: 99px;
  background: $sv-line;
}

.sv-hc__head {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 15px 16px 12px;

  .sv-hc--sheet & { padding-top: 10px; }
}

.sv-hc__who {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.sv-hc__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15.5px;
  font-weight: 800;
  letter-spacing: -0.015em;
}

.sv-hc__age {
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  color: $sv-ink-3;
}

.sv-hc__status {
  flex: 0 0 auto;
  align-self: center;
  margin-left: auto;
  padding: 2px 7px;
  border-radius: 99px;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

@each $key, $c in $sv-pill {
  .sv-hc__status--#{$key} {
    color: map-get($c, shade);
    background: map-get($c, tint);
  }
}

.sv-hc__x {
  flex: 0 0 auto;
  align-self: center;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  margin: -8px -8px -8px 0;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: $sv-bg-3;
  color: $sv-ink-3;
  font-family: inherit;
  cursor: pointer;
}

.sv-hc__when {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 7px;
  font-size: 12px;
  font-weight: 500;
  color: $sv-ink-3;
}

.sv-hc__tm {
  color: $sv-ink-2;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.sv-hc__sep {
  width: 3px;
  height: 3px;
  border-radius: 99px;
  background: $sv-ink-4;
}

.sv-hc__procs {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 3px;
}

.sv-hc__proc {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  height: 22px;
  padding: 0 8px;
  box-sizing: border-box;
  border-radius: 7px;
  background: $sv-bg-2;
  border: 1px solid $sv-line;
  font-size: 11.5px;
  font-weight: 600;
  color: $sv-ink-2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sv-hc__prog {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: $sv-bg-2;
  border-top: 1px solid $sv-line-2;
  border-bottom: 1px solid $sv-line-2;
  font-size: 12px;
  color: $sv-ink-2;

  b {
    font-weight: 800;
    color: $sv-ink;
    font-variant-numeric: tabular-nums;
  }
}

.sv-hc__prog-t {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}

.sv-hc__mut { color: $sv-ink-3; }

.sv-hc__ok {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 700;
  color: $sv-ok-ink;
}

.sv-hc__bar {
  display: flex;
  flex: 0 0 auto;
  gap: 2px;
  width: 110px;
  margin-left: auto;

  i {
    flex: 1;
    height: 5px;
    border-radius: 99px;
    background: $sv-line;
    transition: background 0.2s;

    &.on { background: $sv-brand; }
  }
}

.sv-hc__body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px 8px;
  overflow-y: auto;
  // ⛔ A scroll that reaches the end of the card must not carry on scrolling the schedule behind it.
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  // The fade says "there is more below" without a scrollbar's width.
  &--more {
    -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 30px), transparent);
    mask-image: linear-gradient(to bottom, #000 calc(100% - 30px), transparent);
  }
}

.sv-hc-cat {
  flex: 0 0 auto;
  padding: 7px 6px 5px;
  border-radius: 11px;
  transition: background 0.15s, box-shadow 0.15s;
}

.sv-hc-cat__h {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  margin-bottom: 3px;
}

.sv-hc-cat__tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 10px 0 7px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;

  .fa { font-size: 11px; }
}

.sv-hc-cat__sp { flex: 1; }

.sv-hc-cat__done {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  color: $sv-ok-ink-soft;
}

.sv-hc-cat__go {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: $sv-ink-3;
  font-family: inherit;
  font-size: 11px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s, background 0.12s;
}

// The arrow appears when its section is hovered or highlighted; on touch it is always there.
.sv-hc-cat:hover .sv-hc-cat__go,
.sv-hc-cat--focus .sv-hc-cat__go,
.sv-hc-cat__go:focus-visible {
  opacity: 1;
}

.sv-hc-cat__go:focus { outline: none; }
.sv-hc-cat__go:focus-visible {
  outline: 2px solid $sv-brand-ring;
  outline-offset: 1px;
}

// One colour family per category (tokens: $sv-cat, baked from the design's oklch values).
@each $key, $c in $sv-cat {
  .sv-hc-cat--#{$key} {
    .sv-hc-cat__tag {
      background: map-get($c, tint);
      color: map-get($c, shade);
    }

    .sv-hc-cat__go:hover {
      background: map-get($c, tint);
      color: map-get($c, shade);
    }

    &.sv-hc-cat--focus {
      background: map-get($c, focus-bg);
      box-shadow: inset 0 0 0 1px map-get($c, focus-ring);
    }
  }

  .sv-hc--touch .sv-hc-cat--#{$key} .sv-hc-cat__go {
    background: map-get($c, tint);
    color: map-get($c, shade);
  }
}

.sv-hc-cat__items {
  display: flex;
  flex-direction: column;
}

.sv-hc-item {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) auto;
  column-gap: 9px;
  align-items: start;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 5px 6px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
}

.sv-hc-item--act {
  cursor: pointer;

  &:hover {
    background: $sv-bg-2;

    .sv-hc-cb { border-color: $sv-brand; }
  }

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand-ring;
    outline-offset: -2px;
  }
}

.sv-hc-cb {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  box-sizing: border-box;
  border: 1.5px solid $sv-ink-4;
  border-radius: 5px;
  background: $sv-surface;
  color: #ffffff;
  transition: background 0.12s, border-color 0.12s;

  .fa { font-size: 10px; line-height: 1; }
}

.sv-hc-item--done {
  .sv-hc-cb {
    background: $sv-brand;
    border-color: $sv-brand;

    .fa { animation: sv-hc-tick 0.22s cubic-bezier(0.3, 1.6, 0.5, 1); }
  }

  // ⛔ `.sv-hc-txt .t`, not a bare `.t`: the base title rule further down is `.sv-hc-txt .t`, the
  // same specificity as `.sv-hc-item--done .t`, and it comes LATER — so it won and a ticked item
  // kept its black title (found in a real browser; jsdom tests cannot see a cascade).
  .sv-hc-txt .t {
    color: $sv-ink-3;
    text-decoration: line-through;
    text-decoration-color: $sv-ink-4;
  }

  .s,
  .sv-hc-meta { opacity: 0.55; }
}

.sv-hc-fyi {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  border-radius: 99px;
  background: $sv-amber-soft;
  color: $sv-fyi-ink;
  font-size: 10.5px;
  font-weight: 800;
  line-height: 1;

  &--crit {
    background: $sv-rose;
    color: #ffffff;
  }
}

.sv-hc-txt {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;

  .t {
    font-size: 12.5px;
    font-weight: 600;
    line-height: 16px;
    color: $sv-ink;
  }

  .s {
    font-size: 11.5px;
    line-height: 15px;
    color: $sv-ink-3;
  }
}

.sv-hc-item--info {
  .t {
    font-weight: 500;
    color: $sv-ink-2;
  }

  &.sv-hc-item--crit .t {
    font-weight: 700;
    color: $sv-crit-ink;
  }
}

.sv-hc-meta {
  margin-top: 1px;
  padding: 0 6px;
  border-radius: 6px;
  background: $sv-bg-3;
  color: $sv-ink-2;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;

  &--late {
    background: $sv-rose-soft;
    color: $sv-late-ink;
  }

  &--amt {
    padding: 0;
    background: none;
    color: $sv-ink;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
  }
}

.sv-hc__clear {
  display: flex;
  align-items: center;
  gap: 11px;
  margin: 0 16px 14px;
  padding: 11px 12px;
  border-radius: 11px;
  background: $sv-emerald-soft;
  border: 1px solid $sv-emerald-line;

  .ic {
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 99px;
    background: $sv-status-confirmed;
    color: #ffffff;
    font-size: 12px;
  }

  .t {
    font-size: 12.5px;
    font-weight: 700;
    color: $sv-ok-ink-deep;
  }

  .s {
    font-size: 11.5px;
    color: $sv-ink-3;
  }

  // "We don't know yet" is not "all clear": neutral, not green.
  &--unsure {
    background: $sv-bg-2;
    border-color: $sv-line;

    .ic { background: $sv-ink-4; }
    .t { color: $sv-ink-2; }
  }
}

.sv-hc__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-top: 1px solid $sv-line-2;

  .sv-hc--sheet & { padding: 8px 10px 16px; }
}

.sv-hc__open {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: $sv-brand;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: $sv-brand-soft; }
  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand-ring;
    outline-offset: -2px;
  }

  .fa { font-size: 11px; }
}

// Touch (pop / sheet): bigger rows and targets — a finger, not a pointer. Rows are at least 44px.
.sv-hc--touch {
  .sv-hc-item {
    grid-template-columns: 20px minmax(0, 1fr) auto;
    min-height: 44px;
    padding: 9px 8px;
  }

  .sv-hc-cb {
    width: 20px;
    height: 20px;
    border-radius: 6px;

    .fa { font-size: 12px; }
  }

  .sv-hc-fyi {
    width: 20px;
    height: 20px;
    font-size: 12px;
  }

  .sv-hc-txt .t {
    font-size: 13.5px;
    line-height: 18px;
  }

  .sv-hc-txt .s {
    font-size: 12px;
    line-height: 16px;
  }

  .sv-hc-cat__tag {
    height: 26px;
    font-size: 12.5px;
  }

  .sv-hc-cat__go {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    opacity: 1;
  }

  .sv-hc__open {
    height: 44px;
    font-size: 13.5px;
  }
}
</style>
